import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const FeedbackButtons = ({ 
  userId, 
  agentType, 
  actionId = null, 
  outputSummary = null,
  context = null,
  size = "sm",
  className = ""
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // 'up' or 'down'

  const handleFeedback = async (rating) => {
    if (submitted) return; // Already submitted
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        userId,
        agentType,
        rating, // 1 = thumbs down, 2 = thumbs up
        actionId,
        outputSummary: outputSummary?.substring(0, 500), // Limit summary length
        context
      });
      
      setSubmitted(rating === 2 ? 'up' : 'down');
      toast.success(rating === 2 ? "Thanks for the positive feedback!" : "Thanks, we'll improve!");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  // Determine button sizing
  const isLarge = size === "default" || size === "lg";
  const buttonSize = isLarge ? "h-9 w-9" : "h-7 w-7";
  const iconSize = isLarge ? "h-5 w-5" : "h-4 w-4";

  if (submitted) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className={`${isLarge ? 'text-sm' : 'text-xs'} text-gray-400`}>
          {submitted === 'up' ? 'Thanks!' : 'Noted!'}
        </span>
        {submitted === 'up' ? (
          <ThumbsUp className={`${iconSize} text-green-500 fill-green-500`} />
        ) : (
          <ThumbsDown className={`${iconSize} text-red-500 fill-red-500`} />
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="feedback-buttons">
      <Button
        onClick={() => handleFeedback(2)}
        disabled={submitting}
        size={size}
        variant="ghost"
        className={`${buttonSize} p-0 text-gray-400 hover:text-green-500 hover:bg-green-500/10 transition-colors`}
        title="Good result"
        data-testid="thumbs-up-btn"
      >
        {submitting ? (
          <Loader2 className={`${iconSize} animate-spin`} />
        ) : (
          <ThumbsUp className={iconSize} />
        )}
      </Button>
      <Button
        onClick={() => handleFeedback(1)}
        disabled={submitting}
        size={size}
        variant="ghost"
        className={`${buttonSize} p-0 text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors`}
        title="Needs improvement"
        data-testid="thumbs-down-btn"
      >
        {submitting ? (
          <Loader2 className={`${iconSize} animate-spin`} />
        ) : (
          <ThumbsDown className={iconSize} />
        )}
      </Button>
    </div>
  );
};

export default FeedbackButtons;
